import React, { useState, useEffect, KeyboardEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Box,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { useSnackbar } from 'notistack';
import { UserTemplateItem } from '../../../types/types';
import TemplatePicker from '../../../components/Pickers/TemplatePicker';
import { CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import { useCreateAppointmentType, useUpdateAppointmentType } from '../store/hooks';
import {
  AppointmentType,
  CreateAppointmentTypeRequest,
  UpdateAppointmentTypeRequest,
} from '../types';
import { useAvailableColors } from '../hooks';
import { ColorDot } from './ColorDot';
import ExpandableField, { ExpandableFieldItem, SchedulingData } from './ExpandableField';
import { useEventTracking } from '@services/statsig';
import { useFeatureFlag } from '@features/user';

interface FormScheduleItem extends ExpandableFieldItem {
  form_definition_id: string;
  name: string;
  offset: string;
  timing?: string;
  title?: string;
}

interface ReminderItem extends ExpandableFieldItem {
  reminder_template_id: string;
  name: string;
  offset: string;
  timing?: string;
}

interface AppointmentTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (appointmentType: AppointmentType) => void;
  type: 'create' | 'edit';
  // For create mode
  initialName?: string;
  defaultValues?: {
    basedOnAppointmentType?: AppointmentType;
    overrides?: {
      duration?: number;
      templateId?: string;
      telehealth?: boolean;
      forms?: ExpandableFieldItem[];
      reminders?: ExpandableFieldItem[];
    };
  };
  // For edit mode
  appointmentType?: AppointmentType | null;
}

const APPOINTMENT_REMINDER = {
  id: 'appointment-reminder',
  reminder_template_id: 'xxx',
  name: 'Appointment Reminder',
};

const AppointmentTypeModal: React.FC<AppointmentTypeModalProps> = ({
  open,
  onClose,
  onSave,
  type,
  initialName = '',
  defaultValues,
  appointmentType,
}) => {
  const { createNewAppointmentType, isCreating, createError } = useCreateAppointmentType();
  const { updateAppointmentType, isUpdating, updateError } = useUpdateAppointmentType();
  const availableColors = useAvailableColors();
  const { appointment, error: trackError } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const isPayerTemplateProcessingEnabled = useFeatureFlag('payer_template_note_processing');

  const isLoading = type === 'create' ? isCreating : isUpdating;
  const error = type === 'create' ? createError : updateError;

  const [enterKeyReleased, setEnterKeyReleased] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    color: 'color1' as string,
    defaultTemplateId: null as string | null,
    defaultTemplate: null as UserTemplateItem | null,
    defaultDuration: null as number | null,
    cptCodes: ['', ''] as string[],
    telehealth: false,
    selectedForms: [] as FormScheduleItem[],
    selectedReminders: [] as ReminderItem[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [initialColor, setInitialColor] = useState<string | null>(null);

  // Track modal opened
  useEffect(() => {
    if (open) {
      appointment.typeModalOpened({
        mode: type,
        appointment_type_id: type === 'edit' ? appointmentType?.encounter_type_id : undefined,
      });
    }
  }, [open, type, appointmentType?.encounter_type_id, appointment]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (type === 'create') {
        // Extract values with overrides taking precedence
        const baseType = defaultValues?.basedOnAppointmentType;
        const overrides = defaultValues?.overrides;

        const templateId = overrides?.templateId ?? baseType?.template_id ?? null;
        const duration = overrides?.duration ?? baseType?.default_duration ?? null;
        const telehealth = overrides?.telehealth ?? baseType?.telehealth ?? false;
        const color = baseType?.color || 'color1';

        // Handle CPT codes
        const baseCptCodes = baseType?.default_cpt_codes || [];
        const cptCodes = [baseCptCodes[0] || '', baseCptCodes[1] || ''];

        // For forms and reminders, use overrides if provided, otherwise extract from baseType
        const forms =
          overrides?.forms ??
          (baseType?.default_form_rules?.map((rule, index) => ({
            id: `form-${index}`,
            name: rule.title || rule.form_definition_id || `Form ${index + 1}`,
            form_definition_id: rule.form_definition_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            formId: rule.form_definition_id,
            status: 'configured' as const,
          })) ||
            []);

        const reminders =
          overrides?.reminders ??
          (baseType?.default_reminder_rules?.map((rule, index) => ({
            id: `reminder-${index}`,
            name: rule.title || rule.reminder_template_id || `Reminder ${index + 1}`,
            reminder_template_id: APPOINTMENT_REMINDER.reminder_template_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            status: 'configured' as const,
          })) ||
            []);

        setFormData({
          name: initialName,
          color: color,
          defaultTemplateId: templateId,
          defaultTemplate: null,
          defaultDuration: duration,
          cptCodes: cptCodes,
          telehealth: telehealth,
          selectedForms: forms.map(
            form =>
              ({
                ...form,
                form_definition_id: (form as any).form_definition_id || form.formId || '',
                offset: form.offset || '',
              }) as FormScheduleItem,
          ),
          selectedReminders: reminders.map(
            reminder =>
              ({
                ...reminder,
                reminder_template_id: APPOINTMENT_REMINDER.reminder_template_id,
                offset: reminder.offset || '',
              }) as ReminderItem,
          ),
        });
        setInitialColor(color);
      } else if (type === 'edit' && appointmentType) {
        // Edit mode - populate from existing appointment type
        const forms =
          appointmentType.default_form_rules?.map((rule, index) => ({
            id: `form-${index}`,
            name: rule.title || rule.form_definition_id || `Form ${index + 1}`,
            form_definition_id: rule.form_definition_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            formId: rule.form_definition_id,
            status: 'configured' as const,
          })) || [];

        const reminders =
          appointmentType.default_reminder_rules?.map((rule, index) => ({
            id: `reminder-${index}`,
            name: rule.title || rule.reminder_template_id || `Reminder ${index + 1}`,
            reminder_template_id: APPOINTMENT_REMINDER.reminder_template_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            status: 'configured' as const,
          })) || [];

        const baseCptCodes = appointmentType.default_cpt_codes || [];
        const cptCodes = [baseCptCodes[0] || '', baseCptCodes[1] || ''];

        const currentColor = appointmentType.color || 'color1';
        setFormData({
          name: appointmentType.encounter_type,
          color: currentColor,
          defaultTemplateId: appointmentType.template_id || null,
          defaultTemplate: null,
          defaultDuration: appointmentType.default_duration || null,
          cptCodes: cptCodes,
          telehealth: appointmentType.telehealth || false,
          selectedForms: forms,
          selectedReminders: reminders,
        });
        setInitialColor(currentColor);
      }
      setErrors({});
    }
  }, [open, type, initialName, defaultValues, appointmentType]);

  const handleSave = async () => {
    // Validate required fields
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Appointment type name is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    if (type === 'create') {
      // Create mode
      const appointmentTypeData: CreateAppointmentTypeRequest = {
        encounter_type: formData.name,
        color: formData.color,
        default_duration: formData.defaultDuration || undefined,
        ...(isPayerTemplateProcessingEnabled && {
          default_cpt_codes: formData.cptCodes.filter(code => code.trim() !== ''),
        }),
        telehealth: formData.telehealth,
        template_id: formData.defaultTemplateId,
        default_form_rules: formData.selectedForms.map(form => ({
          form_definition_id: form.form_definition_id,
          offset: form.offset,
        })),
        default_reminder_rules: formData.selectedReminders.map(reminder => ({
          reminder_template_id: reminder.reminder_template_id,
          offset: reminder.offset,
        })),
      };

      try {
        const result = await createNewAppointmentType(appointmentTypeData);
        if (result) {
          // Track successful creation
          appointment.typeCreated({
            appointment_type_id: result.encounter_type_id,
            color_selected: formData.color,
            has_default_duration: !!formData.defaultDuration,
            has_default_template: !!formData.defaultTemplateId,
            has_default_forms: formData.selectedForms.length > 0,
            has_default_reminders: formData.selectedReminders.length > 0,
            has_default_cpt_codes: formData.cptCodes.some(code => code.trim() !== ''),
            is_telehealth: formData.telehealth,
          });

          enqueueSnackbar('Appointment type created successfully', { variant: 'success' });

          if (onSave) {
            onSave(result);
          }
          onClose();
        }
      } catch (e) {
        trackError.track(e as Error, 'appointment_type_creation', {
          color_selected: formData.color,
          has_default_duration: String(!!formData.defaultDuration),
        });
        enqueueSnackbar('Failed to create appointment type', { variant: 'error' });
      }
    } else if (type === 'edit' && appointmentType) {
      // Edit mode
      const updateData: UpdateAppointmentTypeRequest = {
        encounter_type: formData.name,
        color: formData.color,
        default_duration: formData.defaultDuration || undefined,
        ...(isPayerTemplateProcessingEnabled && {
          default_cpt_codes: formData.cptCodes.filter(code => code.trim() !== ''),
        }),
        telehealth: formData.telehealth,
        template_id: formData.defaultTemplateId,
        default_form_rules: formData.selectedForms.map(form => ({
          form_definition_id: form.form_definition_id,
          offset: form.offset,
        })),
        default_reminder_rules: formData.selectedReminders.map(reminder => ({
          reminder_template_id: reminder.reminder_template_id,
          offset: reminder.offset,
        })),
      };

      // Determine what fields changed
      const fieldsChanged: string[] = [];
      if (formData.name !== appointmentType.encounter_type) fieldsChanged.push('name');
      if (formData.color !== initialColor) fieldsChanged.push('color');
      if (formData.defaultDuration !== appointmentType.default_duration)
        fieldsChanged.push('duration');
      if (
        JSON.stringify(formData.cptCodes.filter(c => c)) !==
        JSON.stringify(appointmentType.default_cpt_codes || [])
      )
        fieldsChanged.push('cpt_codes');
      if (formData.telehealth !== appointmentType.telehealth) fieldsChanged.push('telehealth');
      if (formData.defaultTemplateId !== appointmentType.template_id)
        fieldsChanged.push('template');
      if (
        JSON.stringify(formData.selectedForms) !==
        JSON.stringify(appointmentType.default_form_rules)
      )
        fieldsChanged.push('forms');
      if (
        JSON.stringify(formData.selectedReminders) !==
        JSON.stringify(appointmentType.default_reminder_rules)
      )
        fieldsChanged.push('reminders');

      try {
        const result = await updateAppointmentType(appointmentType.encounter_type_id, updateData);
        if (result) {
          // Track successful update
          appointment.typeUpdated({
            appointment_type_id: appointmentType.encounter_type_id,
            color_changed: formData.color !== initialColor,
            color_selected: formData.color,
            fields_changed: fieldsChanged,
          });

          enqueueSnackbar('Appointment type updated successfully', { variant: 'success' });

          if (onSave) {
            onSave(result);
          }
          onClose();
        }
      } catch (e) {
        trackError.track(e as Error, 'appointment_type_update', {
          appointment_type_id: appointmentType.encounter_type_id,
          fields_changed: fieldsChanged.join(','),
        });
        enqueueSnackbar('Failed to update appointment type', { variant: 'error' });
      }
    }
  };

  // Handle template selection
  const handleTemplateSelect = (template: UserTemplateItem | undefined) => {
    setFormData(prev => ({
      ...prev,
      defaultTemplateId: template?.template_id || null,
      defaultTemplate: template || null,
    }));
  };

  const handleAddForm = (schedulingData: SchedulingData) => {
    const offset = convertTimingToOffset(schedulingData.timing, true, schedulingData.customTiming);

    const newForm: FormScheduleItem = {
      id: Date.now().toString(),
      form_definition_id: schedulingData.formId || schedulingData.name,
      name: schedulingData.name,
      title: schedulingData.name,
      offset: offset,
      timing: schedulingData.timing,
    };

    setFormData(prev => ({
      ...prev,
      selectedForms: [...prev.selectedForms, newForm],
    }));
  };

  const handleUpdateForm = (formId: string | number, schedulingData: SchedulingData) => {
    const offset = convertTimingToOffset(schedulingData.timing, true, schedulingData.customTiming);

    setFormData(prev => ({
      ...prev,
      selectedForms: prev.selectedForms.map(form =>
        form.id === formId
          ? {
              ...form,
              form_definition_id: schedulingData.formId || schedulingData.name,
              name: schedulingData.name,
              title: schedulingData.name,
              offset: offset,
              timing: schedulingData.timing,
            }
          : form,
      ),
    }));
  };

  const handleRemoveForm = (formId: string | number) => {
    setFormData(prev => ({
      ...prev,
      selectedForms: prev.selectedForms.filter(form => form.id !== formId),
    }));
  };

  const handleAddReminder = (schedulingData: SchedulingData) => {
    const offset = convertTimingToOffset(schedulingData.timing, false, schedulingData.customTiming);

    const newReminder: ReminderItem = {
      id: Date.now().toString(),
      reminder_template_id: APPOINTMENT_REMINDER.reminder_template_id,
      name: APPOINTMENT_REMINDER.name,
      offset: offset,
      timing: schedulingData.timing,
    };

    setFormData(prev => ({
      ...prev,
      selectedReminders: [...prev.selectedReminders, newReminder],
    }));
  };

  const handleUpdateReminder = (reminderId: string | number, schedulingData: SchedulingData) => {
    const offset = convertTimingToOffset(schedulingData.timing, false, schedulingData.customTiming);

    setFormData(prev => ({
      ...prev,
      selectedReminders: prev.selectedReminders.map(reminder =>
        reminder.id === reminderId
          ? {
              ...reminder,
              reminder_template_id: APPOINTMENT_REMINDER.reminder_template_id,
              name: APPOINTMENT_REMINDER.name,
              offset: offset,
              timing: schedulingData.timing,
            }
          : reminder,
      ),
    }));
  };

  const handleRemoveReminder = (reminderId: string | number) => {
    setFormData(prev => ({
      ...prev,
      selectedReminders: prev.selectedReminders.filter(reminder => reminder.id !== reminderId),
    }));
  };

  // Helper function to convert timing strings to ISO 8601 duration format
  const convertTimingToOffset = (
    timing: string,
    isForm: boolean,
    customTiming?: string,
  ): string => {
    if (customTiming) {
      return customTiming;
    }

    if (isForm) {
      switch (timing) {
        case 'Immediately before appointment':
          return 'PT0H';
        case '1 hour before appointment':
          return '-PT1H';
        case '1 day before appointment':
          return '-P1D';
        case '2 days before appointment':
          return '-P2D';
        case '1 week before appointment':
          return '-P7D';
        case 'Immediately after appointment':
          return 'PT0H';
        case '1 hour after appointment':
          return 'PT1H';
        case '1 day after appointment':
          return 'P1D';
        default:
          return timing;
      }
    } else {
      switch (timing) {
        case '1 hour before appointment':
          return '-PT1H';
        case '2 hours before appointment':
          return '-PT2H';
        case '1 day before appointment':
          return '-P1D';
        case '2 days before appointment':
          return '-P2D';
        case '1 week before appointment':
          return '-P7D';
        default:
          return timing;
      }
    }
  };

  // Handle Enter key press and release to submit form
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      if (
        enterKeyReleased &&
        !event.shiftKey &&
        !event.ctrlKey &&
        formData.name.trim() &&
        !isLoading
      ) {
        event.preventDefault();
        handleSave();
      }
      setEnterKeyReleased(false);
    }
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      setEnterKeyReleased(true);
    }
  };

  // Don't render if edit mode but no appointment type
  if (type === 'edit' && !appointmentType) {
    return null;
  }

  const dialogTitle = type === 'create' ? 'Create new Appointment Type' : 'Edit Appointment Type';
  const submitButtonText =
    type === 'create' ? 'Create Appointment Type' : 'Update Appointment Type';
  const loadingText = type === 'create' ? 'Creating...' : 'Updating...';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: '90vh',
        },
      }}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Error Display */}
          {error && (
            <Alert severity="error">Failed to {type} appointment type. Please try again.</Alert>
          )}

          {/* Encounter Type Name and Color */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <TextField
              label="Name (e.g. Intake 60 min)"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ flex: 1, minWidth: '200px' }}
              fullWidth
              autoFocus
              required
            />

            <FormControl sx={{ minWidth: 80 }}>
              <InputLabel>Color</InputLabel>
              <Select
                value={formData.color}
                onChange={e => {
                  const newColor = e.target.value;
                  const previousColor = formData.color;
                  setFormData(prev => ({ ...prev, color: newColor }));

                  // Track color change
                  appointment.typeColorChanged({
                    appointment_type_id:
                      type === 'edit' ? appointmentType?.encounter_type_id : undefined,
                    previous_color: previousColor,
                    new_color: newColor,
                    context: type,
                  });
                }}
                label="Color"
                renderValue={value => (
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <ColorDot colorEnum={value} size={20} />
                  </Box>
                )}
              >
                {availableColors.map(option => (
                  <MenuItem key={option.value} value={option.value} aria-label={option.label}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <ColorDot colorEnum={option.value} size={20} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Default Template */}
          <TemplatePicker
            initialTemplateId={formData.defaultTemplateId}
            onTemplateSelect={handleTemplateSelect}
            required={false}
            label="Default Note Template"
          />

          {/* Default Duration */}
          <TextField
            label="Default Duration (minutes)"
            type="number"
            value={formData.defaultDuration || ''}
            onChange={e => {
              const value = parseInt(e.target.value, 10);
              setFormData(prev => ({
                ...prev,
                defaultDuration: isNaN(value) || value <= 0 ? null : value,
              }));
            }}
            inputProps={{
              min: 1,
              max: 480,
              step: 5,
            }}
            helperText="Enter duration in minutes (e.g., 30, 45, 60)"
            fullWidth
          />

          {/* Default CPT Codes */}
          {isPayerTemplateProcessingEnabled && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Default CPT Codes (Optional)
              </Typography>
              <CPTCodePicker
                value={formData.cptCodes}
                onChange={codes => setFormData(prev => ({ ...prev, cptCodes: codes }))}
                showHeader={false}
                size="small"
              />
            </Box>
          )}

          {/* Default Telehealth */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.telehealth}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    telehealth: e.target.checked,
                  }))
                }
              />
            }
            label="Make telehealth the default"
          />

          {/* Default Forms */}
          <ExpandableField
            title="Attach Default Form(s)"
            items={formData.selectedForms}
            onAddItem={handleAddForm}
            onUpdateItem={handleUpdateForm}
            onDeleteItem={handleRemoveForm}
            isFormType={true}
            mode={type}
          />

          {/* Default Reminders */}
          <ExpandableField
            title="Default Reminders"
            items={formData.selectedReminders}
            onAddItem={handleAddReminder}
            onUpdateItem={handleUpdateReminder}
            onDeleteItem={handleRemoveReminder}
            isFormType={false}
            mode={type}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={isLoading}>
          Cancel
        </Button>
        <Tooltip title={!formData.name.trim() ? 'Enter appointment type name' : ''} placement="top">
          <span>
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!formData.name.trim() || isLoading}
                sx={{
                  color: 'white',
                  paddingRight: formData.name.trim() && !isLoading ? '30px' : '16px',
                }}
                startIcon={isLoading ? <CircularProgress size={16} /> : null}
              >
                {isLoading ? loadingText : submitButtonText}
              </Button>
              {formData.name.trim() && !isLoading && (
                <KeyboardReturnIcon
                  fontSize="small"
                  sx={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '1rem',
                    opacity: 0.7,
                    color: 'white',
                  }}
                />
              )}
            </Box>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentTypeModal;
